import { PageLoader } from "src/components/PageLoader/PageLoader";
import type { ReactNode } from "react";
import { AccessDeniedPage } from "src/pages/AccessDeniedPage/AccessDeniedPage.page";
import { SignInFailedPage } from "src/pages/SignInFailedPage/SignInFailedPage.page";
import { endAuth0Session, useAuthGuardState } from "../core";
import { useAuth } from "../hooks/useAuth";
import { useSignInRecovery } from "../hooks/useSignInRecovery";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function AuthProtectedRoute({ children }: ProtectedRouteProps) {
  const auth = useAuth();
  const guardState = useAuthGuardState(auth);
  const recovery = useSignInRecovery();

  // If Auth0 is disabled, pass through
  if (!auth.isEnabled) {
    return <>{children}</>;
  }

  // Show loading spinner while auth state settles / redirect starts
  if (guardState === "loading") {
    return <PageLoader label="Loading" />;
  }

  // Auth0 reported an error (e.g. a root callback URL came back with ?error=):
  // show it, instead of redirecting to sign-in again, which would loop.
  if (guardState === "failed") {
    return (
      <SignInFailedPage
        message={auth.error?.message}
        onTryAgain={recovery.tryAgain}
        onSignOut={recovery.signOut}
      />
    );
  }

  // Access Denied UI
  if (guardState === "denied") {
    return <AccessDeniedPage onSignOut={() => endAuth0Session(auth.logout)} />;
  }

  // User is authenticated and has required claims
  return <>{children}</>;
}
